import React, { useState, useEffect, useRef } from "react";
import "../Styles/ZonesContent.css";
import {
  Dropdown,
  DefaultButton,
  PrimaryButton,
  Dialog,
  DialogType,
  Panel,
  PanelType,
  Toggle,
  SpinButton
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import Axios from "axios";

function normalizeToMaxZones(devicesMatrix, maxZones) {
  // devicesMatrix shape: loops × devices × [z1,z2,z3,z4]
  return devicesMatrix.map(loopArr =>
    loopArr.map(row => {
      const zones = row.filter(z => z !== 0).sort((a, b) => a - b);
      const keep = zones.slice(0, maxZones);          // keep lowest N
      const padded = [...keep, 0, 0, 0].slice(0, 4);  // always length 4
      return padded;
    })
  );
}

const normalizeCoincidenceValue = (value) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) return 1;
  return Math.min(255, Math.max(1, Math.trunc(parsedValue)));
};

const ZonesContent = ({ project, panelVersion }) => {
  const GenerateDeviceAddressName = (loop, address) => {
    const addr = address < 10 ? "00" + address.toString() : address < 100 ? "0" + address.toString() : address.toString();
    return `L${loop.toString()}M${addr}`
  }

  const ParseDeviceAddressName = (deviceName) => {
    const match = deviceName.match(/^L(\d+)M(\d{3})$/);
    if (!match) return null; // Handle invalid format
  
    const loop = parseInt(match[1], 10);    // "1" → 1
    const address = parseInt(match[2], 10); // "003" → 3
  
    return { loop, address };
  };

  const [selectedLoop, setSelectedLoop] = useState(0);
  const [selectedZone, setSelectedZone] = useState(null); // Store selected device index
  const [isDevicesDialogOpen, { setTrue: openDevicesDialog, setFalse: dismissDevicesDialog }] = useBoolean(false);
  const [isWeeklyTrendDialogOpen, { setTrue: openWeeklyTrendDialog, setFalse: dismissWeeklyTrendDialog }] = useBoolean(false);
  const [zoneName, setZoneName] = useState("");
  const [weeklyTrend, setWeeklyTrend] = useState(0);
  const [isAllSystem, setIsAllSystem] = useState(false);
  const [alarm, setAlarm] = useState(true);
  const [alarmCoincidence, setAlarmCoincidence] = useState(1);
  const [fault, setFault] = useState(false);
  const [faultCoincidence, setFaultCoincidence] = useState(1);
  const [technical, setTechnical] = useState(false);
  const [technicalCoincidence, setTechnicalCoincidence] = useState(1);
  const [loop, setLoop] = useState(0);
  const [devices, setDevices] = useState(
    Array(9).fill(0).map(() =>
      Array(240).fill(0).map(() =>
        Array(4).fill(0)  // Set all elements to 0 instead of ""
      )
    )
  );
  const currentPanelVersion = panelVersion ?? "2.0.F7";
  const maxZonesPerDevice = currentPanelVersion === "2.0.F7" ? 4 : 1;

  const prevVersionRef = useRef(currentPanelVersion);
  const prevMaxRef = useRef(maxZonesPerDevice);

  useEffect(() => {
    const prevV = prevVersionRef.current;
    const prevMax = prevMaxRef.current;
    prevVersionRef.current = currentPanelVersion;
    prevMaxRef.current = maxZonesPerDevice;

    // If the new version allows fewer zones (e.g., → 2.7.F9), collapse now
    if (maxZonesPerDevice < prevMax) {
      setDevices(prev => normalizeToMaxZones(prev, maxZonesPerDevice));
    }
  }, [currentPanelVersion, maxZonesPerDevice, setDevices]);

  if (!project || !project.panel || Object.keys(project).length === 0) {
    return <div>No panel data found.</div>;
  }

  const loopCardNb = project.panel.VT5000_CONFIG.GeneralParameters?.loopCardNb || 0;
  const loopButtonsCount = 1 + 2 * loopCardNb;

  const handleZoneButtonClick = (index) => {
    const selectedZoneData = project.panel.VT5000_CONFIG.ZoneTable[index];
    const storedAlarm = selectedZoneData?.alarm === 1;
    const storedFault = selectedZoneData?.fault === 1;
    const storedTechnical = selectedZoneData?.technical === 1;
    const hasNoActiveEventType = !storedAlarm && !storedFault && !storedTechnical;

    setSelectedZone(index);
    setSelectedLoop(0);
    setZoneName((selectedZoneData?.name ?? "").replace(/\u0000/g, ''));
    setWeeklyTrend(selectedZoneData?.weeklyTrendDisablement ?? 0);
    setIsAllSystem(selectedZoneData?.allSystem === 1);
    setAlarm(storedAlarm || hasNoActiveEventType);
    setAlarmCoincidence(normalizeCoincidenceValue(selectedZoneData?.alarmCoincidence));
    setFault(storedFault);
    setFaultCoincidence(normalizeCoincidenceValue(selectedZoneData?.faultCoincidence));
    setTechnical(storedTechnical);
    setTechnicalCoincidence(normalizeCoincidenceValue(selectedZoneData?.technicalCoincidence));
    setLoop(selectedZoneData?.loop ?? 0);
    setDevices(
      Array(9).fill(0).map(() =>
        Array(240).fill(0).map(() =>
          Array(4).fill(0)
        )
      )
    );

    {
      const newDevices = Array(9).fill(0).map(() =>
        Array(240).fill(0).map(() => Array(4).fill(0))
      );

      for (let i = 0; i < project.panel.VT5000_CONFIG.ArgusTablePr.length; i++) {
        for (let j = 0; j < project.panel.VT5000_CONFIG.ArgusTablePr[i].length; j++) {
          const row = project.panel.VT5000_CONFIG.ArgusTablePr[i][j];
          const zones = [row.zone, row.zone2, row.zone3, row.zone4];
          const filteredZones = zones.map(z => (z === index + 1 ? z : 0));
          newDevices[i][j] = zones;
        }
      }

      setDevices(newDevices); // Only call once
    }
    openDevicesDialog();
  };

  const handleDeviceButtonClick = (loopIndex, deviceIndex) => {
    const panelData = project.panel.VT5000_CONFIG.ArgusTablePr;

    const newDevices = devices.map(loop =>
      loop.map(device => [...device]) // deep clone
    );

    const addZone = (i, j) => {
      let row = newDevices[i][j];

      // collect existing non-zero zones + the new one
      const zones = Array.from(new Set(row.filter(z => z !== 0).concat(selectedZone + 1)));
      // keep the lowest N according to current version (1 if 2.7.F9, otherwise 4)
      const keep = zones.sort((a, b) => a - b).slice(0, maxZonesPerDevice);
      // pad to 4
      const padded = [...keep, 0, 0, 0].slice(0, 4);
      newDevices[i][j] = padded;
    };

    const removeZone = (i, j) => {
      let row = newDevices[i][j].filter(val => val !== selectedZone + 1);
      while (row.length < 4) row.push(0);
      row.sort((a, b) => (a === 0 ? 1 : b === 0 ? -1 : 0));
      newDevices[i][j] = row;
    };

    const isValidDevice = (device, row) => {
      const isProgrammed = device?.programmed === 1;
      const isModule = isProgrammed && device.deviceType === 1;
      const isDevice = isProgrammed && device.deviceType === 0;
      const isSmoke = isDevice && device.lastTipo === 0 && device.isMultiSensor === 0;
      const isThermal = isDevice && device.lastTipo === 1;
      const isSmokeTherm = isDevice && device.lastTipo === 0 && device.isMultiSensor === 1;
      const isDisabled = !(isSmoke || isThermal || isSmokeTherm || (isModule && device.lastTipo === 3)) || (row.filter(val => val === 0).length === 0 && !row.includes(selectedZone + 1));
      return !isDisabled;
    };

    if (isAllSystem) {
      for (let i = 0; i < panelData.length; i++) {
        for (let j = 0; j < panelData[i].length; j++) {
          if (i === loopIndex && j === deviceIndex) continue;
          const device = panelData[i][j];
          const row = newDevices[i][j];
          if (isValidDevice(device, row)) {
            addZone(i, j);
          }
        }
      }

      // Unselect clicked device
      removeZone(loopIndex, deviceIndex);

      // ✅ Reset group mode
      setIsAllSystem(false);
    } else if (loop > 0) {
      const i = loop - 1;
      for (let j = 0; j < panelData[i].length; j++) {
        if (i === loopIndex && j === deviceIndex) continue;
        const device = panelData[i][j];
        const row = newDevices[i][j];
        if (isValidDevice(device, row)) {
          addZone(i, j);
        }
      }

      // Unselect clicked device
      removeZone(loopIndex, deviceIndex);

      // ✅ Reset loop mode
      setLoop(0);
    } else {
      // Normal toggle behavior
      let row = newDevices[loopIndex][deviceIndex];

      if (row.includes(selectedZone + 1)) {
        // REMOVE this zone (toggle off)
        row = row.filter(val => val !== (selectedZone + 1));
        while (row.length < 4) row.push(0);
      } else {
        // ADD this zone, respecting maxZonesPerDevice (1 for 2.7.F9, 4 for 2.0.F7)
        const zones = Array.from(new Set(row.filter(z => z !== 0).concat(selectedZone + 1)));
        const keep = zones.sort((a, b) => a - b).slice(0, maxZonesPerDevice);
        row = [...keep, 0, 0, 0].slice(0, 4);
      }

      row.sort((a, b) => (a === 0 ? 1 : b === 0 ? -1 : 0));
      newDevices[loopIndex][deviceIndex] = row;
    }

    setDevices(newDevices);
  };

  const clearSelectedZoneFromDevices = (prevDevices) => {
    return prevDevices.map(loop =>
      loop.map(row => {
        const updated = row.filter(val => val !== selectedZone + 1);
        while (updated.length < 4) updated.push(0);
        return updated.sort((a, b) => (a === 0 ? 1 : b === 0 ? -1 : 0));
      })
    );
  };

  const handleLoopSelected = () => {
    // Clear all device assignments for current selected zone
    setDevices((prevDevices) => clearSelectedZoneFromDevices(prevDevices));
    // Toggle loop selection
    if (loop > 0 && loop === selectedLoop + 1) {
      setLoop(0);
    } else {
      setIsAllSystem(false);
      setLoop(selectedLoop + 1);
    }
  };

  const handleIsAllSystemToggle = () => {
    setDevices((prevDevices) => clearSelectedZoneFromDevices(prevDevices));
    setLoop(0);
    setIsAllSystem(!isAllSystem);
  }

  const handleWeeklyTrendBlockClick = (index) => {
    setWeeklyTrend(index + 1);
    dismissWeeklyTrendDialog();
  };

  const onSave = () => {
    if (selectedZone >= 0) {
      const alarmToSave = alarm || (!fault && !technical);

      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].loop = loop;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].name = zoneName;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].alarm = alarmToSave ? 1 : 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].alarmCoincidence = normalizeCoincidenceValue(alarmCoincidence);
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].fault = fault ? 1 : 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].faultCoincidence = normalizeCoincidenceValue(faultCoincidence);
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].technical = technical ? 1 : 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].technicalCoincidence = normalizeCoincidenceValue(technicalCoincidence);
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].allSystem = isAllSystem ? 1 : 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].programmed = 1;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].weeklyTrendDisablement = weeklyTrend;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].disablement = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].disablementCoincidence = 1;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].preAlarm = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].preAlarmCoincidence = 1;
      if (loop > 0 || isAllSystem > 0) {
        for (let i = 0; i < project.panel.VT5000_CONFIG.ArgusTablePr.length; i++) {
          for (let j = 0; j < project.panel.VT5000_CONFIG.ArgusTablePr[i].length; j++) {
            if (project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone === selectedZone + 1) {
              project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone = 0;
            }
            if (project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone2 === selectedZone + 1) {
              project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone2 = 0;
            }
            if (project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone3 === selectedZone + 1) {
              project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone3 = 0;
            }
            if (project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone4 === selectedZone + 1) {
              project.panel.VT5000_CONFIG.ArgusTablePr[i][j].zone4 = 0;
            }
          }
        }
      }
      else {
        const devicesToPersist = normalizeToMaxZones(devices, maxZonesPerDevice);

        for (let i = 0; i < project.panel.VT5000_CONFIG.ArgusTablePr.length; i++) {
          for (let j = 0; j < project.panel.VT5000_CONFIG.ArgusTablePr[i].length; j++) {
            const device = project.panel.VT5000_CONFIG.ArgusTablePr[i][j];
            const row = devicesToPersist[i]?.[j] || [0, 0, 0, 0];  // <-- use devicesToPersist
            device.zone = row[0] ?? 0;
            device.zone2 = row[1] ?? 0;
            device.zone3 = row[2] ?? 0;
            device.zone4 = row[3] ?? 0;
          }
        }
      }
    }

    const userEmail = localStorage.getItem('userEmail');
    Axios.post(`${process.env.REACT_APP_BACKEND_URL}/update_panel_zones`, {
      projectId: project.project_id,
      zones: project.panel.VT5000_CONFIG.ZoneTable,
      argusTablePr: project.panel.VT5000_CONFIG.ArgusTablePr
    })
      .then((response) => {
        if (response.data.success) {
          console.log("Zones updated successfully");
          dismissDevicesDialog();
        } else {
          console.error("Failed to update Zones");

        }
      })
      .catch((error) => {
        console.error("Error updating Zones:", error);
      });
  };

  const onRemove = () => {
    if (selectedZone >= 0) {
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].loop = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].name = "";
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].alarm = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].alarmCoincidence = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].fault = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].faultCoincidence = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].technical = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].technicalCoincidence = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].allSystem = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].programmed = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].weeklyTrendDisablement = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].disablement = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].disablementCoincidence = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].preAlarm = 0;
      project.panel.VT5000_CONFIG.ZoneTable[selectedZone].preAlarmCoincidence = 0;
      for (let i = 0; i < project.panel.VT5000_CONFIG.ArgusTablePr.length; i++) {
        for (let j = 0; j < project.panel.VT5000_CONFIG.ArgusTablePr[i].length; j++) {
          const device = project.panel.VT5000_CONFIG.ArgusTablePr[i][j];

          if (device.zone === selectedZone + 1) device.zone = 0;
          if (device.zone2 === selectedZone + 1) device.zone2 = 0;
          if (device.zone3 === selectedZone + 1) device.zone3 = 0;
          if (device.zone4 === selectedZone + 1) device.zone4 = 0;
        }
      }
      setDevices(
        Array(9).fill(0).map(() =>
          Array(240).fill(0).map(() =>
            Array(4).fill(0)
          )
        )
      );
    }

    const userEmail = localStorage.getItem('userEmail');
    Axios.post(`${process.env.REACT_APP_BACKEND_URL}/update_panel_zones`, {
      projectId: project.project_id,
      zones: project.panel.VT5000_CONFIG.ZoneTable,
      argusTablePr: project.panel.VT5000_CONFIG.ArgusTablePr
    })
      .then((response) => {
        if (response.data.success) {
          console.log("Zones updated successfully");
          dismissDevicesDialog();
        } else {
          console.error("Failed to update Zones");

        }
      })
      .catch((error) => {
        console.error("Error updating Zones:", error);
      });
  };

  return (
    <div className="zone-content">
      {/* Zones Buttons */}
      <div className="zone-buttons-container">
        <div className="zone-buttons-frame">
          {Array.from({ length: project.panel.VT5000_CONFIG.ZoneTable.length }, (_, index) => {
            const i = index; // Zone index
            const zone = project.panel.VT5000_CONFIG.ZoneTable?.[i];
            const isProgrammed = zone?.programmed === 1;

            let buttonClass = isProgrammed ? "programmed" : "not-programmed"; // Default class

            return (
              <button
                key={index}
                className={`zone-button ${buttonClass}`}
                onClick={() => handleZoneButtonClick(index)}
              >
                <span>{index + 1}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Dialog
        hidden={!isDevicesDialogOpen}
        onDismiss={dismissDevicesDialog}
        dialogContentProps={{
          type: DialogType.normal,
        }}
        minWidth={"1000px"}
      >
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
              <label htmlFor="nameInput">Zone:</label>
              <input
                id="nameInput"
                type="text"
                value={zoneName}
                onChange={(e) => setZoneName(e.target.value)}
                placeholder={zoneName}
              />
              <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "32px" }}>
                <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}>All System:</span>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Toggle
                    styles={{
                      root: { marginBottom: "0px", padding: "0px" },
                      label: { display: "none" }, // Hide Fluent UI’s built-in label
                      pill: { marginTop: "0px", alignSelf: "center" },
                      thumb: { marginTop: "0px" },
                    }}
                    checked={isAllSystem}
                    onChange={() => { handleIsAllSystemToggle() }}
                    onText="Yes"
                    offText="No"
                  />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "32px" }}>
                <span style={{ fontSize: "16px" }}>Loop Selected:</span>
                <button
                  style={{ fontSize: "16px", background: "#E0E0E0", borderRadius: "5px", border: "1px solid black", height: "30px", width: "50px", textAlign: "center" }}
                  onClick={() => handleLoopSelected()}
                >
                  {loop}
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px" }}>
                <span style={{ fontSize: "16px" }}>Weekly Trend Autoexclusion:</span>
                <DefaultButton text={weeklyTrend} onClick={() => openWeeklyTrendDialog()} />
              </div>
              <Dialog
                hidden={!isWeeklyTrendDialogOpen}
                onDismiss={() => dismissWeeklyTrendDialog()}
                minWidth={"1000px"}
                dialogContentProps={{
                  type: DialogType.normal,
                  title: "Weekly Trend",
                }}
              >
                <div className="weeklyTrend-buttons-container">
                  <div className="weeklyTrend-buttons-frame">
                    {Array.from({ length: project.panel.VT5000_CONFIG.WeeklyTrends.length }, (_, i) => {
                      const isProgrammed = project.panel.VT5000_CONFIG.WeeklyTrends[i]?.programmed === 1;
                      return (
                        <button
                          key={i}
                          className={`weeklyTrend-button ${isProgrammed ? "programmed" : "not-programmed"} ${weeklyTrend === i + 1 ? "selected" : ""
                            }`}
                          onClick={() => handleWeeklyTrendBlockClick(i)}
                          disabled={!isProgrammed}
                        >
                          <span>{i + 1}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Dialog>
            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", height: "32px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "32px", marginRight: "54px" }}>
                  <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}>Alarm:</span>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Toggle
                      styles={{
                        root: { marginBottom: "0px", padding: "0px" },
                        label: { display: "none" }, // Hide Fluent UI’s built-in label
                        pill: { marginTop: "0px", alignSelf: "center" },
                        thumb: { marginTop: "0px" },
                      }}
                      checked={alarm}
                      onChange={() => setAlarm(!alarm)}
                      onText="Yes"
                      offText="No"
                    />
                  </div>
                </div>
                <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}>Alarm Coincidence:</span>
                <div style={{ display: "flex", alignItems: "center", width: "100px" }}>
                  <SpinButton
                    min={1}
                    max={255}
                    value={alarmCoincidence.toString()} // Ensure the value is a string
                    onChange={(event, newValue) => {
                      const parsedValue = parseInt(newValue, 10);
                      if (!isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= 255) {
                        setAlarmCoincidence(parsedValue);
                      }
                    }}
                    styles={{
                      root: { marginBottom: "0px", padding: "0px", width: "100%" },
                      spinButtonWrapper: { height: "32px", display: "flex", alignItems: "center" },
                      input: { textAlign: "center", fontSize: "14px" },
                    }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", height: "32px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "32px", marginRight: "62px" }}>
                  <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}>Fault:</span>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Toggle
                      styles={{
                        root: { marginBottom: "0px", padding: "0px" },
                        label: { display: "none" }, // Hide Fluent UI’s built-in label
                        pill: { marginTop: "0px", alignSelf: "center" },
                        thumb: { marginTop: "0px" },
                      }}
                      checked={fault}
                      onChange={() => setFault(!fault)}
                      onText="Yes"
                      offText="No"
                    />
                  </div>
                </div>
                <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}>Fault Coincidence:</span>
                <div style={{ display: "flex", alignItems: "center", width: "100px" }}>
                  <SpinButton
                    min={1}
                    max={255}
                    value={faultCoincidence.toString()} // Ensure the value is a string
                    onChange={(event, newValue) => {
                      const parsedValue = parseInt(newValue, 10);
                      if (!isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= 255) {
                        setFaultCoincidence(parsedValue);
                      }
                    }}
                    styles={{
                      root: { marginBottom: "0px", padding: "0px", width: "100%" },
                      spinButtonWrapper: { height: "32px", display: "flex", alignItems: "center" },
                      input: { textAlign: "center", fontSize: "14px" },
                    }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", height: "32px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "32px" }}>
                  <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}>Technological:</span>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Toggle
                      styles={{
                        root: { marginBottom: "0px", padding: "0px" },
                        label: { display: "none" }, // Hide Fluent UI’s built-in label
                        pill: { marginTop: "0px", alignSelf: "center" },
                        thumb: { marginTop: "0px" },
                      }}
                      checked={technical}
                      onChange={() => setTechnical(!technical)}
                      onText="Yes"
                      offText="No"
                    />
                  </div>
                </div>
                <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}>Technological Coincidence:</span>
                <div style={{ display: "flex", alignItems: "center", width: "100px" }}>
                  <SpinButton
                    min={1}
                    max={255}
                    value={technicalCoincidence.toString()} // Ensure the value is a string
                    onChange={(event, newValue) => {
                      const parsedValue = parseInt(newValue, 10);
                      if (!isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= 255) {
                        setTechnicalCoincidence(parsedValue);
                      }
                    }}
                    styles={{
                      root: { marginBottom: "0px", padding: "0px", width: "100%" },
                      spinButtonWrapper: { height: "32px", display: "flex", alignItems: "center" },
                      input: { textAlign: "center", fontSize: "14px" },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="devices-content">
            {/* Loop Buttons */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
              <div className="loop-buttons">
                <span>Loop:</span>
                {Array.from({ length: loopButtonsCount }, (_, index) => (
                  <button
                    key={index}
                    className={`loop-button ${selectedLoop === index ? "selected-loop" : ""}`}
                    onClick={() => setSelectedLoop(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <PrimaryButton text="Save" onClick={() => onSave()} />
                <DefaultButton text="Remove" onClick={() => onRemove()} />
                <DefaultButton text="Back" onClick={dismissDevicesDialog} />
              </div>
            </div>

            {/* Device Buttons */}
            <div className="device-buttons-container">
              <div className="device-buttons-frame">
                {Array.from({ length: project.panel.VT5000_CONFIG.ArgusTablePr[0].length }, (_, index) => {
                  const i = selectedLoop; // Loop index
                  const j = index;        // Device index

                  const device = project.panel.VT5000_CONFIG.ArgusTablePr?.[i]?.[j];
                  const isProgrammed = device?.programmed === 1;
                  const isModule = isProgrammed && (device.deviceType === 1);
                  const isDevice = isProgrammed && (device.deviceType === 0);
                  const isSmoke = isDevice && (device.lastTipo === 0) && (device.isMultiSensor === 0);
                  const isThermal = isDevice && (device.lastTipo === 1);
                  const isSmokeTherm = isDevice && (device.lastTipo === 0) && (device.isMultiSensor === 1);

                  const currentZone = project.panel.VT5000_CONFIG.ZoneTable[selectedZone];

                  const item = devices[i][j]; // [z1,z2,z3,z4]

                  // NEW: disable in single-zone mode if assigned to a different zone
                  const isSingleZoneMode = maxZonesPerDevice === 1;
                  const assignedZone = item.find(z => z !== 0) ?? 0;
                  const singleZoneDisable =
                    isSingleZoneMode &&
                    assignedZone !== 0 &&
                    assignedZone !== (selectedZone + 1);

                  // your existing disabled rule
                  const baseDisabled =
                    !(isSmoke || isThermal || isSmokeTherm || (isModule && (device.lastTipo === 3))) ||
                    (item.filter(val => val === 0).length === 0 && !item.includes(selectedZone + 1));

                  const isDisabled = baseDisabled || singleZoneDisable;

                  const isSelected =
                    !isDisabled &&
                    (isSmoke || isThermal || isSmokeTherm || (isModule && (device.lastTipo === 3))) &&
                    (isAllSystem || loop === selectedLoop + 1 || item.includes(selectedZone + 1));

                  let buttonClass = "not-programmed"; // Default class
                  if (isModule) buttonClass = "module";
                  else if (isSmoke) buttonClass = "smoke";
                  else if (isThermal) buttonClass = "heat";
                  else if (isSmokeTherm) buttonClass = "smokeTherm";

                  return (
                    <button
                      key={index}
                      className={`device-button ${buttonClass} ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                      disabled={isDisabled}
                      title={singleZoneDisable ? `Already assigned to Zone ${assignedZone}` : ""}
                      onClick={() => !isDisabled && handleDeviceButtonClick(i, j)}
                    >
                      <span>{index + 1}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ZonesContent;
